/**
 * Resolve the local HTML date key represented by a Flatpickr day cell.
 *
 * @param {HTMLElement & {dateObj?: Date}} day
 * @param {import('flatpickr/dist/types/instance').Instance} instance
 * @return {string|null}
 */
function getFlatpickrDayDateKey(day, instance) {
    if (day.dateObj instanceof Date) {
        return instance.formatDate(day.dateObj, 'Y-m-d');
    }

    if (day.dataset.date) {
        return day.dataset.date;
    }

    if (day.ariaLabel) {
        const ariaDate = instance.parseDate(day.ariaLabel, instance.config.ariaDateFormat);

        if (ariaDate instanceof Date) {
            return instance.formatDate(ariaDate, 'Y-m-d');
        }
    }

    return null;
}

/**
 * Remove event-date decoration from a Flatpickr day cell.
 *
 * @param {HTMLElement} day
 */
function resetCalendarEventDate(day) {
    day.classList.remove('calendar-event-date');
    day.classList.remove('calendar-event-date-single-color');
    day.classList.remove('calendar-event-date-mixed-color');
    day.removeAttribute('data-event-date-color');
    day.style.removeProperty('--calendar-event-date-color');
    day.style.removeProperty('--calendar-event-date-text-color');
}

/**
 * Decorate one Flatpickr day cell from backend-provided metadata.
 *
 * @param {HTMLElement & {dateObj?: Date}} day
 * @param {import('flatpickr/dist/types/instance').Instance} instance
 * @param {Object<string, {displayColor: string, displayTextColor: string, isMixed: boolean}>} metadataByDate
 */
function decorateCalendarEventDateDay(day, instance, metadataByDate) {
    const dateKey = getFlatpickrDayDateKey(day, instance);

    if (!dateKey) {
        return;
    }

    day.dataset.date = dateKey;
    resetCalendarEventDate(day);

    const metadata = metadataByDate[dateKey];
    if (!metadata) {
        return;
    }

    day.classList.add('calendar-event-date');
    day.classList.add(metadata.isMixed ? 'calendar-event-date-mixed-color' : 'calendar-event-date-single-color');
    day.dataset.eventDateColor = metadata.displayColor;
    day.style.setProperty('--calendar-event-date-color', metadata.displayColor);
    day.style.setProperty('--calendar-event-date-text-color', metadata.displayTextColor);
}

/**
 * Decorate every visible Flatpickr day cell from current metadata.
 *
 * @param {import('flatpickr/dist/types/instance').Instance|null} instance
 * @param {Object<string, {displayColor: string, displayTextColor: string, isMixed: boolean}>} metadataByDate
 */
function decorateCalendarEventDates(instance, metadataByDate) {
    if (!instance || !instance.calendarContainer) {
        return;
    }

    instance.calendarContainer.querySelectorAll('.flatpickr-day').forEach(day => {
        decorateCalendarEventDateDay(day, instance, metadataByDate);
    });
}

export {
    decorateCalendarEventDateDay,
    decorateCalendarEventDates,
    getFlatpickrDayDateKey,
    resetCalendarEventDate,
};
